import { useCallback, useContext } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { LeadManagementContext } from '../contexts/LeadManagementContextProvider';
import { leadManagementMinimizedMessagingConversationIdStore } from '../lib/leadManagementStores';
import { getConversationMessagesQueryKey } from './useConversationMessagesQuery';

/**
 * Leave the minimized state, refreshing the minimized conversation's thread. While minimized the
 * thread is unmounted, so neither it nor useRealtimeUpdates (which skips the active conversation
 * on purpose) refetches messages that arrive in the meantime — and showing that thread again
 * within the provider staleTime would render it without them. Invalidate the *minimized*
 * conversation (the only one that can be stale — a closed window's conversation is non-active and
 * realtime-invalidated) before clearing, so the fix holds even when the window being opened is a
 * different conversation, or the window unmounts while minimized. Runs on every path that leaves
 * the minimized state: the pill's restore, explicit opens from the table/drawer, and unmount.
 */
export default function useRestoreMessagingWindow() {
  const queryClient = useQueryClient();
  const { clientTimeZone } = useContext(LeadManagementContext);

  return useCallback(() => {
    const minimizedConversationId = leadManagementMinimizedMessagingConversationIdStore.get();
    if (minimizedConversationId) {
      queryClient.invalidateQueries({
        queryKey: getConversationMessagesQueryKey({ conversationId: minimizedConversationId, clientTimeZone }),
      });
    }
    leadManagementMinimizedMessagingConversationIdStore.set('');
  }, [queryClient, clientTimeZone]);
}
